<template>
  <div v-if="roster" :class="baseClass">
    <template v-if="showHeader">
      <h5 class="text-sm font-medium mb-1 leading-none">
        {{ $t('roster.normalHours') }}
      </h5>
      <p class="mb-12 text-sm opacity-80">
        {{ $t('components.employee.edit.roster.normalHoursInfo') }}
      </p>
    </template>

    <div v-if="showFrequency" class="mb-6">
      <o-field-group :label="$t('general.frequency')" :info="$t('roster.rosterInterval')" col class="mb-3 block">
        <segment-field v-model="frequency" :options="frequencyOptions" />
      </o-field-group>
    </div>

    <div :class="showFrequency || showHeader ? 'mt-12' : ''">
      <div v-if="showFrequency" class="mb-3 flex items-center justify-between">
        <div v-if="week">
          <div class="flex items-center space-x-2 mb-1">
            <template v-if="!isRosterFrequencyWeekly">
              <p class="opacity-90 font-medium">
                {{ $t('roster.week') }}
              </p>
              <o-chip size="xs" variant="success" flat>
                {{ week.weekLabel }}
              </o-chip>
            </template>

            <p v-else class="opacity-90 font-medium">
              {{ $t('general.roster') }}
            </p>
          </div>
          <p class="opacity-80 text-sm">
            {{ $t('roster.starting', { date: week.dateLabel }) }}
          </p>
        </div>

        <div>
          <div v-if="frequency && !isRosterFrequencyWeekly">
            <segment-field v-model="week" :options="weekOptions" />
          </div>
        </div>
      </div>
      <o-table
        v-if="week"
        class="w-full"
        container-class=""
        :data="roster.weeks[week.week - 1].days"
        :headers="headers"
        :limit="20"
        striped
        collapse
        scroll
      >
        <template #row="props">
          <td :class="props.tdClass">
            <toggle-field v-model="props.row.hour.working" />
          </td>
          <td :class="props.tdClass">
            {{ props.row.dayLabel }}
          </td>
          <td :class="props.tdClass">
            <text-field v-model="props.row.hour.label" :disabled="!showLabel && props.row.hour.working" />
          </td>
          <td :class="props.tdClass">
            <o-field-group v-slot="{ status }" :name="$t('general.startTime')">
              <time-field
                v-model="props.row.hour.startTime"
                :augmenter="Augmenters.minutes"
                :status="status"
              />
            </o-field-group>
          </td>
          <td :class="props.tdClass">
            <o-field-group
              v-slot="{ status }"
              :name="$t('roster.finishTime')"
              :status="isInvalidWorkingHour(props.row.hour) ? 'error' : null"
              :message="isInvalidWorkingHour(props.row.hour) ? $t('roster.invalidHours') : null"
            >
              <time-field
                v-model="props.row.hour.finishTime"
                :augmenter="Augmenters.minutes"
                :status="isInvalidWorkingHour(props.row.hour) ? 'error' : status"
              />
            </o-field-group>
          </td>
          <td :class="props.tdClass">
            <div clas="flex items-center">
              <p class="inline mr-2 text-sm">
                {{ props.row.breaks.length }}
              </p>
              <o-button flat size="xs" @click="props.toggle">
                {{ $t('general.edit') }}
              </o-button>
            </div>
          </td>
        </template>

        <template #collapse="{ row }">
          <breaks v-model:breaks="row.breaks" :day="row" />
        </template>
      </o-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import Breaks from '@/components/roster/elements/edit/RosterEditBreaks.vue'

import {
  OTable,
} from '@/components/elements'

import {
  SegmentField,
  TimeField,
  CheckboxField,
  TextField,
  ToggleField,
} from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'
import { isInvalidWorkingHour } from '@/utils/roster'

interface Props {
  employee: Record<string, any>
  roster: Record<string, any>
  showHeader?: boolean
  showFrequency?: boolean
  showLabel?: boolean
  baseClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  showHeader: true,
  showFrequency: true,
  showLabel: true,
  baseClass: 'w-full px-6 py-4',
})

const emit = defineEmits<{
  (e: 'update:roster', value: Record<string, any>): void
}>()

const { t } = useI18n()

const frequency = ref<any>(null)
const week = ref<any>(null)

const _roster = computed({
  get () {
    return props.roster
  },
  set (value) {
    emit('update:roster', value)
  },
})

const frequencyOptions = computed(() => {
  const roster = props.roster
  const frequencies = roster.frequencies

  return frequencies
    .map((item: any) => {
      return {
        label: item.label,
        value: item,
      }
    })
})

const weekOptions = computed(() => {
  const freq = frequency.value
  const weeks = freq.currentWeeks

  return weeks
    .sort((a: any, b: any) => a.week - b.week)
    .map((item: any) => {
      return {
        label: item.weekLabel,
        value: item,
      }
    })
})

const headers = computed(() => {
  const items = [
    { text: ' ' },
    { text: t('datetime.labels.day') },
  ]

  // if (props.showLabel) {
  items.push(
    { text: t('general.label') },
  )
  // }

  items.push(
    { text: t('general.startTime') },
    { text: t('roster.finishTime') },
    { text: t('roster.breaks') },
  )

  return items
})

const isRosterFrequencyWeekly = computed(() => {
  return props.roster.isRosterFrequencyWeekly
})

const isRosterFrequencyFortnightly = computed(() => {
  return props.roster.isRosterFrequencyFortnightly
})

const isRosterFrequencyMonthly = computed(() => {
  return props.roster.isRosterFrequencyMonthly
})

watch(() => props.roster, (roster) => {
  if (roster) {
    const freq = roster.frequency
    const frequencies = roster.frequencies

    const match = frequencies.find((item: any) => item.frequency === freq)

    frequency.value = match
  }
}, { immediate: true })

watch(frequency, (value) => {
  if (value) {
    const freq = value.frequency
    const currentWeeks = value.currentWeeks

    _roster.value.frequency = freq
    week.value = currentWeeks[0]
  }
}, { immediate: true })
</script>
